/* eslint-disable no-underscore-dangle */
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PaymentsService from '../Services/PaymentsService';

const errorText = (err) => err?.response?.data?.message || err?.message || 'Failed to load payments';

/**
 * One booking's Payment rows (`GET /payments?booking_id=`, newest first) and
 * the local updates after a write, so the list reflects it without a
 * re-fetch. Order Details reads it once for its payment history and the
 * Order Ledger tab's paid / outstanding.
 *
 * Fetched whenever `enabled` turns true (e.g. each time a modal opens); a
 * response that lands after a newer request started is dropped. `loaded` is
 * true once THIS booking's list is in — until then `payments` is an empty
 * list that means "not known", not "nothing paid".
 */
export default function useBookingPayments(bookingId, { enabled = true } = {}) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The booking whose list `payments` is.
  const [loadedFor, setLoadedFor] = useState(null);
  const seqRef = useRef(0);

  const reload = useCallback(async () => {
    if (!bookingId) return;
    const mySeq = seqRef.current + 1;
    seqRef.current = mySeq;
    setLoading(true);
    setError(null);
    try {
      const rows = await PaymentsService.listForBooking(bookingId);
      if (mySeq === seqRef.current) {
        setPayments(Array.isArray(rows) ? rows : []);
        setLoadedFor(bookingId);
      }
    } catch (err) {
      if (mySeq !== seqRef.current) return;
      setError(errorText(err));
      setPayments([]);
      setLoadedFor(null);
    } finally {
      if (mySeq === seqRef.current) setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  /** A payment just recorded — the newest, so it goes on top. */
  const recorded = (payment) => {
    if (payment) setPayments((prev) => [payment, ...prev]);
  };

  /**
   * A refund just written. The API returns only the offset row, with no
   * "was it full" flag, so a full refund is inferred from the gross amounts
   * and the original is flipped to 'refunded' locally, as the API does.
   * Returns `{ original, wasFullRefund }` for callers with more to update.
   */
  const refunded = (offset, originalId) => {
    if (!offset) return { original: null, wasFullRefund: false };
    const original = payments.find((p) => p?._id === originalId) || null;
    const offsetGross = Number(offset.gross_amount);
    const originalGross = Number(original?.gross_amount);
    // 0.005 covers two-decimal rounding without letting genuinely different
    // amounts (199.99 vs 200) collapse.
    const wasFullRefund = original != null
      && !Number.isNaN(offsetGross)
      && !Number.isNaN(originalGross)
      && Math.abs(offsetGross - originalGross) < 0.005;
    setPayments((prev) => {
      const next = [offset, ...prev];
      return wasFullRefund
        ? next.map((p) => (p?._id === originalId ? { ...p, status: 'refunded' } : p))
        : next;
    });
    return { original, wasFullRefund };
  };

  /** A payment changed in place (e.g. its allocation re-cut). */
  const replaced = (updated) => {
    setPayments((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
  };

  const loaded = loadedFor === bookingId && !loading;

  return {
    payments, loaded, loading, error, setError, reload, recorded, refunded, replaced,
  };
}
